import type {
  ConditionData,
  ConditionGroupData,
  ConditionGroupMap,
  ConditionMap,
  RuleData,
  RuleMap
} from "./constraints.types";
import { CompareType } from "./constraints.types";
import type { FormInstance } from "antd/lib/form";
import { isEqual as lodashIsEqual } from "lodash-es";
import { uuidWithProcessId } from "../../../utils/db.utils";
import { ruleActionDeserialize } from "../../../utils/ruleActions";
import type { OptionItem } from "@certa/types";
import { RuleType } from "@certa/studio-core";

type RawCondition = { id: string; compare_group: string } & Record<string, any>;

/**
 * These are the keys that we do not count as "actions", i.e. these keys
 * only define the condition part of the constraint.
 */
const constraintKeys = [
  "operator",
  // this is used in c2p/p2c so we're now including that in actions,
  // hence this commented out here.
  // "target_field",
  "__cs_comment",
  "compare_group",
  "compare_value",
  "first_compare_field",
  "second_compare_field"
];

const isEqual = (
  dict1: Record<string, any>,
  dict2: Record<string, any>,
  keys: string[] = []
) => {
  const providedKeysLength = keys.length;
  const d1Keys = providedKeysLength ? keys : Object.keys(dict1);
  const d2Keys = providedKeysLength ? keys : Object.keys(dict2);

  if (d1Keys.length !== d2Keys.length) return false;

  return d1Keys.every(key => lodashIsEqual(dict1[key], dict2[key]));
};

/**
 * Extracts set of unique rules that have their own unique set of actions.
 * Conditions and groups to be extracted out separately
 * @param constraints
 */
const extractRules = (
  constraints: Record<string, any>,
  processId: string,
  ruleType: RuleType,
  targetFieldTag?: string
) => {
  /**
   * Here we break a single constraint object into two parts,
   * - actions
   * - condition
   *
   * Every unique "set" of action forms a unique "rule".
   */
  const rules: RuleData[] = []; // to store rules
  const ruleMap: Record<string, RawCondition[]> = {}; // to store conditions
  Object.values(constraints).forEach((constraint: any) => {
    const actions = { ...constraint };
    delete actions.depth;
    const condition: RawCondition = {
      id: uuidWithProcessId(processId),
      depth: constraint.depth,
      compare_group: constraint.compare_group
    };
    Object.keys(actions).forEach(key => {
      if (constraintKeys.includes(key)) {
        condition[key] =
          constraint[key] !== undefined ? String(constraint[key]) : undefined;
        delete actions[key];
      }
    });
    const isConditionOptionalConstraint = !!actions.constraints;
    const optionalConstraintConditions: RawCondition[] = [];
    if (isConditionOptionalConstraint) {
      Object.values(actions.constraints).forEach((constraintValue: any) => {
        const optionalConstraintCondition: RawCondition = {
          id: uuidWithProcessId(processId),
          depth: constraintValue.depth,
          compare_group: constraintValue.compare_group
        };
        Object.keys(constraintValue).forEach(key => {
          if (constraintKeys.includes(key)) {
            optionalConstraintCondition[key] =
              constraintValue[key] !== undefined
                ? String(constraintValue[key])
                : undefined;
          }
        });
        if (optionalConstraintCondition.first_compare_field) {
          optionalConstraintConditions.push(optionalConstraintCondition);
        }
      });
      delete actions.constraints;
    }

    // If the rule we extracted, already exists in our rules array, then
    // we just need to get it. Uniqueness of rule is defined by the
    // set of actions it runs.
    const existingRule = rules.find(rule => isEqual(rule.actions, actions));

    if (!existingRule) {
      // if the rule doesn't exist yet, with the particular set of actions
      // we simply create that rule.
      const rule: RuleData = {
        id: uuidWithProcessId(processId),
        actions,
        conditions: {},
        groups: {},
        topLevelGroupId: "",
        ruleType,
        __createdAt: Date.now(),
        __modifiedAt: Date.now()
      };
      if (targetFieldTag) {
        rule.__targetFieldTag = targetFieldTag;
      }
      rules.push(rule);

      // Every condition, is bound to a particular rule, which is why
      // we temporarily add the rule ID to the condition, so it becomes
      // easier to group them later on.
      if (
        isConditionOptionalConstraint &&
        optionalConstraintConditions.length > 0
      ) {
        ruleMap[rule.id] = [...optionalConstraintConditions];
      } else {
        ruleMap[rule.id] = condition.first_compare_field ? [condition] : [];
      }
    } else {
      // when we have an existing rule, we just mention its ID within the
      // condition itself for the reason mentioned above.
      if (
        isConditionOptionalConstraint &&
        optionalConstraintConditions.length > 0
      ) {
        ruleMap[existingRule.id].push(...optionalConstraintConditions);
      } else {
        if (condition.first_compare_field) {
          ruleMap[existingRule.id].push(condition);
        }
      }
    }
  });

  // finally we return the unique rules and conditions.
  return { rules, ruleMap };
};

const rawToCondition = (
  condition: RawCondition,
  processId: string
): ConditionData => {
  return {
    id: uuidWithProcessId(processId),
    firstCompareField: condition.first_compare_field,
    secondCompareField: condition.second_compare_field,
    compareValue: condition.compare_value,
    operator: condition.operator,
    __createdAt: Date.now(),
    __modifiedAt: Date.now()
  };
};

/**
 *
 * @param ungroupedData
 * @param groups
 * @param groupId
 */
const extractGroups = (
  ungroupedData: RawCondition[],
  processId: string
): {
  conditionMap: ConditionMap;
  groupMap: ConditionGroupMap;
  groups: string[];
} => {
  /**
   * We simply make a parent ANY group
   * and within that we put each compare-group as an AND group.
   *
   * So let's say there were Compare group 1, 2, 3 with 1 & 2 having
   * 4 conditions each, and compare group 3 having just 1 condition,
   * and they were doing same thing, let's say turning
   * visibility of something to true, then we create
   *
   * ANY Group
   * - Condition 9
   * - ALL Group
   *   - Condition 1
   *   - Condition 2
   *   - Condition 3
   *   - Condition 4
   * - All Group
   *   - Condition 5
   *   - Condition 6
   *   - Condition 7
   *   - Condition 8
   */

  const conditionMap: ConditionMap = {};
  const groupMap: ConditionGroupMap = {};
  const groups: string[] = [];

  const compareGroupsMap: Record<string, string[]> = {};

  /**
   * Sort all conditions into groups, also transform them to the
   * Type that FE uses.
   */
  ungroupedData.forEach(condition => {
    const transformedCondition = rawToCondition(condition, processId);
    conditionMap[transformedCondition.id] = transformedCondition;
    if (compareGroupsMap[condition.compare_group]) {
      compareGroupsMap[condition.compare_group].push(transformedCondition.id);
    } else {
      compareGroupsMap[condition.compare_group] = [transformedCondition.id];
    }
  });

  /**
   * Now since each compare group works as an AND group, and on
   * top level we always use ANY group, we create a wrapping ANY
   * group that will house different compare groups as individual AND
   * groups, each having their own condition.
   */
  const topLevelGroup: ConditionGroupData = {
    id: uuidWithProcessId(processId),
    conditions: [],
    groups: [],
    compareType: CompareType.ANY,
    __createdAt: Date.now(),
    __modifiedAt: Date.now()
  };

  // We make sure that the the group we created above is
  // put in the group map.
  groupMap[topLevelGroup.id] = topLevelGroup;

  // And in the groups array (Note, first element of this array
  // is actually used as Top level group)
  // It basically means that this is considered as top node
  // so we should always put the top level group in the 0 index
  groups.push(topLevelGroup.id);

  /**
   * Now for all compare groups, we create ALL groups and
   * link conditions to them.
   */
  Object.keys(compareGroupsMap).forEach(compareGroup => {
    if (compareGroupsMap[compareGroup].length > 1) {
      const andGroup: ConditionGroupData = {
        id: uuidWithProcessId(processId),
        conditions: compareGroupsMap[compareGroup],
        groups: [],
        compareType: CompareType.ALL,
        __createdAt: Date.now(),
        __modifiedAt: Date.now()
      };
      groupMap[andGroup.id] = andGroup;
      topLevelGroup.groups.push(andGroup.id);
      groups.push(andGroup.id);
    } else {
      // If it's one condition in that compare group, we can
      // directly put it in the ANY condition
      topLevelGroup.conditions.push(compareGroupsMap[compareGroup][0]);
    }
  });

  /**
   * Finally we return everything.
   */
  return { conditionMap, groupMap, groups };
};

/**
 * Converts the system specific schema into UI specific data-structure without any
 * dataloss.
 * @param constraints
 */
export const deserialize = (
  constraints: Record<string, any>,
  conditionLess: boolean,
  processId: string,
  ruleType: RuleType,
  targetFieldTag?: string
) => {
  const { rules, ruleMap } = extractRules(
    constraints,
    processId,
    ruleType,
    targetFieldTag
  );
  // conditionGroupMap[topLevelConditionGroup.id] = topLevelConditionGroup;
  // now that we have the unique set of rules and conditions (not unique, yet)
  // we need to iterate over conditions and start grouping them
  const transformedRules: RuleMap = {};
  rules.forEach((rule, index) => {
    // for every rule, we take its conditions and start grouping them.
    const conditions = ruleMap[rule.id];

    const {
      groups = [],
      groupMap = {},
      conditionMap = {}
    } = conditionLess ? {} : extractGroups(conditions, processId);

    const ruleActions = ruleType
      ? ruleActionDeserialize(rule.actions, ruleType)
      : rule.actions;

    const newRule: RuleData = {
      id: rule.id ?? uuidWithProcessId(processId),
      name: `Rule ${index + 1}`,
      actions: ruleActions,
      conditions: conditionMap,
      groups: groupMap,
      topLevelGroupId: groups[0],
      ruleType,
      __createdAt: Date.now(),
      __modifiedAt: Date.now()
    };

    /* If rule.name doesn't exist for notifications, rule name should be equal to notification name   */
    if (ruleType === RuleType.Notification && ruleActions.name) {
      newRule.name = rule.name || ruleActions.name;
    }

    if (targetFieldTag) {
      newRule.__targetFieldTag = targetFieldTag;
    }

    if (conditionLess && conditions.length === 1) {
      newRule.actions = { ...newRule.actions, ...conditions[0] };
    }

    transformedRules[newRule.id] = newRule;
  });
  return transformedRules;
};

/**
 * Simple utility function that will iterate over a given set of Forms and
 * return true and any of those form have any errors in them. If not, then
 * it will return false.
 * @param forms
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formsHaveError = (forms: Set<FormInstance>) => {
  for (const form of Array.from(forms)) {
    if (
      !form.getFieldsError().every(formError => formError.errors.length === 0)
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Update the rules for the non-ruleIndex records.
 * TODO: This would be removed when all the contraint old Data is shifted to index.
 * @param rules
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const checkRuleAndUpdateIfIndexNotFound = (rules: RuleMap) => {
  const rulesKeyArray = Object.keys(rules);
  const isAnyRuleWithoutIndex = rulesKeyArray.some(key => {
    if (rules[key]?.ruleIndex) {
      return false;
    }
    return true;
  });

  if (isAnyRuleWithoutIndex) {
    rulesKeyArray.forEach((key, index) => {
      if (typeof rules[key] === "object") rules[key].ruleIndex = index + 1;
    });
  }

  return rules;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ruleObjectToArrayTransform = (rules: RuleMap) =>
  Object.values(rules).sort((a, b) =>
    a.ruleIndex && b.ruleIndex
      ? a.ruleIndex - b.ruleIndex
      : a.id > b.id
        ? 1
        : -1
  );

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ruleArrayToObjectTransform = (rules: RuleData[]) =>
  rules.reduce((acc, rule) => ({ ...acc, [rule.id]: rule }), {});

/**
 * Flattens the cascader options such that all their values become
 * tilde separated with prefixed ancestors' values.
 * @param options
 * @param leafNodesOnly when true, any parent options will not be in the list
 * @returns
 */
export const cascaderOptionsFlatten = (
  options: OptionItem[],
  leafNodesOnly?: boolean
) => {
  const flattenOptions: OptionItem[] = [];
  const flattenLevelOptions = (
    currentOptions: OptionItem[],
    level = 0,
    parentValue?: string
  ) => {
    currentOptions.forEach(opt => {
      const optValue = parentValue ? `${parentValue}~${opt.value}` : opt.value;
      const optLabel = Array(level).fill("•").join("") + ` ${opt.label}`;
      if (Array.isArray(opt.children)) {
        if (!leafNodesOnly) {
          flattenOptions.push({ label: optLabel, value: optValue });
        }
        flattenLevelOptions(opt.children, level + 1, optValue);
      } else {
        flattenOptions.push({ label: optLabel, value: optValue });
      }
    });
  };

  flattenLevelOptions(options);
  return flattenOptions;
};
